import { collection, doc, getDocs, getDoc, updateDoc, query, where, orderBy, Timestamp, addDoc, deleteDoc, setDoc, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Lead } from '../types/lead.types';
import { DeterminationChange, DeterminationEntry } from '../types/billing.types';
import { Appointment } from '../types/schedule.types';
import { format, startOfWeek } from 'date-fns';
import { billingAdjustmentsService } from './billingAdjustments.service';
import { taskService } from './task.service';
import { projectService } from './project.service';
import { notificationService } from './notification.service';
import { appointmentService } from './appointment.service';

export interface LeadFilters {
  search: string;
  status: string[];
  determination: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  schoolSegmentId?: string;
}

export async function getLeads(filters?: any): Promise<Lead[]> {
  let q = query(collection(db, 'leads'), orderBy('createdAt', 'desc'));

  // Filter by projectId (critical for reports)
  if (filters?.projectId) {
    q = query(q, where('projectId', '==', filters.projectId));
  }

  if (filters?.status) {
    q = query(q, where('status', '==', filters.status));
  }

  if (filters?.determination) {
    q = query(q, where('determination', '==', filters.determination));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Lead));
}

export async function getLead(leadId: string): Promise<Lead | null> {
  try {
    const leadDoc = await getDoc(doc(db, 'leads', leadId));
    if (leadDoc.exists()) {
      return { id: leadDoc.id, ...leadDoc.data() } as Lead;
    }
    return null;
  } catch (error) {
    console.error('Error getting lead:', error);
    return null;
  }
}

// Track determination changes for billing adjustments
async function trackDeterminationChange(
  leadId: string,
  fromDetermination: string,
  toDetermination: string,
  changedBy: string,
  changedByName: string,
  projectId: string,
  parentName: string,
  students: number
): Promise<void> {
  const change: Omit<DeterminationChange, 'id' | 'changedAt'> = {
    leadId,
    projectId,
    previousDetermination: fromDetermination as 'valid' | 'invalid' | 'pending',
    newDetermination: toDetermination as 'valid' | 'invalid' | 'pending',
    changedBy,
    changedByName,
    parentName,
    students, // student (children) count at change time — carry-over bills per student
    weekOfChange: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    billingImpact: calculateBillingImpact(fromDetermination, toDetermination),
    reason: 'Manual update',
    billed: false
  };
  
  // Use the service method which will ensure all data is populated
  await billingAdjustmentsService.createDeterminationChange(change);
}

// Calculate the billing impact of a determination change
function calculateBillingImpact(from: string, to: string): number {
  // Pending to Valid = +1 (new billable lead)
  if (from === 'pending' && to === 'valid') return 1;
  
  // Invalid to Valid = +1 (now billable)
  if (from === 'invalid' && to === 'valid') return 1;
  
  // Valid to Invalid = -1 (no longer billable)
  if (from === 'valid' && to === 'invalid') return -1;
  
  // Valid to Pending = -1 (no longer billable until resolved)
  if (from === 'valid' && to === 'pending') return -1;
  
  // No billing impact for other changes
  return 0;
}

export async function updateLead(
  leadId: string, 
  updates: Partial<Lead>, 
  userId?: string, 
  userName?: string
): Promise<void> {
  const leadRef = doc(db, 'leads', leadId);
  
  // Get current lead data
  const currentLeadDoc = await getDoc(leadRef);
  if (!currentLeadDoc.exists()) {
    throw new Error('Lead not found');
  }
  
  const currentLead = currentLeadDoc.data() as Lead;
  
  if (updates.determination && updates.determination !== currentLead.determination && userId && userName) {
    await trackDeterminationChange(
      leadId,
      currentLead.determination || 'pending',
      updates.determination,
      userId,
      userName,
      currentLead.projectId || '',
      currentLead.parentName,
      currentLead.children?.length || 0
    );
  }
  
  if (updates.determination && userId) {
    const determinationEntry: DeterminationEntry = {
      determination: updates.determination,
      timestamp: Timestamp.now(),
      userId: userId,
      notes: updates.notes || ''
    };
    
    const existingHistory = currentLead.determinationHistory || [];
    updates.determinationHistory = [...existingHistory, determinationEntry];
  }
  
  await updateDoc(leadRef, {
    ...updates,
    lastUpdated: Timestamp.now()
  });
}

export interface CreateLeadOptions {
  leadData: Partial<Lead>;
  // Optional: Create task for client_user
  createTaskForClientUser?: {
    organizationId: string;
    projectName: string;
    clientUserId: string;
    clientUserName: string;
    createdByName: string;
  };
  // Skip notifications when creating lead+appointment together
  skipNotifications?: boolean;
}

export async function createLead(
  leadData: Partial<Lead>,
  options?: {
    createTaskForClientUser?: CreateLeadOptions['createTaskForClientUser'];
    skipNotifications?: boolean;
  }
): Promise<string> {
  const newLead = {
    ...leadData,
    createdAt: Timestamp.now(),
    lastUpdated: Timestamp.now(),
    status: leadData.status || 'uncontacted',
    determination: leadData.determination || 'pending',
    capturedBy: leadData.capturedBy || leadData.createdBy || '',
    capturedByName: leadData.capturedByName || '',
  };

  const docRef = await addDoc(collection(db, 'leads'), newLead);

  // Send in-app notifications to provider_users (non-blocking)
  // Skip if combined notification will be sent (lead + appointment)
  if (leadData.projectId && !options?.skipNotifications) {
    notificationService.sendLeadOnlyNotification({
      leadId: docRef.id,
      leadName: leadData.parentName || 'New Lead',
      projectId: leadData.projectId,
      capturedByName: leadData.capturedByName || 'Field User',
      capturedById: leadData.capturedBy || leadData.createdBy || '',
      phone: leadData.phone,
      email: leadData.email
    }).catch(err => {
      console.error('Error sending lead notifications:', err);
    });
  }

  if (options?.createTaskForClientUser && leadData.projectId) {
    try {
      await taskService.createLeadFollowUpTask({
        organizationId: options.createTaskForClientUser.organizationId,
        projectId: leadData.projectId,
        projectName: options.createTaskForClientUser.projectName,
        leadId: docRef.id,
        leadName: leadData.parentName || 'New Lead',
        assignedTo: options.createTaskForClientUser.clientUserId,
        assignedToName: options.createTaskForClientUser.clientUserName,
        createdBy: leadData.createdBy || '',
        createdByName: options.createTaskForClientUser.createdByName
      });
    } catch (error) {
      console.error('Error creating lead follow-up task:', error);
      // Don't throw - lead was still created successfully
    }
  }

  return docRef.id;
}

// Create lead and appointment together with a single combined notification
export interface CreateLeadWithAppointmentOptions {
  leadData: Partial<Lead>;
  appointmentData: {
    date: string;
    startTime: string;
    endTime: string;
    eventType: 'tour' | 'phone' | 'meeting';
    clientUserId: string;
    clientUserName?: string;
    scheduleId?: string;
    slotCapacity?: number;
    rideProvided?: boolean;
  };
  createdByUserId: string;
  createdByUserName: string;
  projectName?: string;
}

export async function createLeadWithAppointment(
  options: CreateLeadWithAppointmentOptions
): Promise<{ leadId: string; appointmentId: string }> {
  const { leadData, appointmentData, createdByUserId, createdByUserName, projectName } = options;

  const leadId = await createLead(leadData, { skipNotifications: true });

  // Route through appointmentService so capacity enforcement (slotLocks
  // transaction) runs on this path. Throws SlotFullError when full.
  const appointmentId = await appointmentService.createAppointment(
    {
      projectId: leadData.projectId || '',
      leadId,
      leadName: leadData.parentName,
      parentName: leadData.parentName,
      phone: leadData.phone,
      userId: appointmentData.clientUserId,
      // Campus → per-campus slot capacity (3pm at Metro North ≠ 3pm at Maryvale).
      ...(leadData.schoolSegmentId ? { schoolSegmentId: leadData.schoolSegmentId } : {}),
      ...(appointmentData.scheduleId ? { scheduleId: appointmentData.scheduleId } : {}),
      date: appointmentData.date,
      startTime: appointmentData.startTime,
      endTime: appointmentData.endTime,
      eventType: appointmentData.eventType,
      status: 'scheduled',
      ...(appointmentData.rideProvided ? { rideProvided: true } : {}),
    } as Omit<Appointment, 'id' | 'createdAt'>,
    {
      createdByUserId,
      createdByUserName,
      slotCapacity: appointmentData.slotCapacity ?? 1,
      // Combined lead+appointment notification is sent below.
      skipNotifications: true,
    }
  );

  notificationService.sendLeadWithAppointmentNotification(
    {
      leadId,
      leadName: leadData.parentName || 'New Lead',
      projectId: leadData.projectId || '',
      projectName,
      capturedByName: createdByUserName,
      capturedById: createdByUserId,
      phone: leadData.phone,
      email: leadData.email
    },
    {
      appointmentId: appointmentId,
      leadId,
      leadName: leadData.parentName || 'New Lead',
      projectId: leadData.projectId || '',
      projectName,
      date: appointmentData.date,
      startTime: appointmentData.startTime,
      endTime: appointmentData.endTime,
      eventType: appointmentData.eventType,
      clientUserId: appointmentData.clientUserId,
      clientUserName: appointmentData.clientUserName,
      scheduledByName: createdByUserName,
      scheduledById: createdByUserId
    }
  ).catch(err => {
    console.error('Error sending combined notifications:', err);
  });

  return { leadId, appointmentId: appointmentId };
}

// Schedule a follow-up appointment for an existing lead.
// Delegates to appointmentService.createAppointment so capacity enforcement
// (slotLocks transaction) runs on this path too. Throws SlotFullError if the
// requested slot has hit its capacity.
export async function scheduleFollowUpAppointment(
  leadId: string,
  appointmentData: {
    date: string;
    startTime: string;
    endTime: string;
    eventType: 'tour' | 'phone' | 'meeting';
    clientUserId: string;
    clientUserName?: string;
    scheduleId?: string;
    slotCapacity?: number;
    rideProvided?: boolean;
  },
  scheduledByUserId: string,
  scheduledByUserName: string
): Promise<string> {
  const lead = await getLead(leadId);
  if (!lead) {
    throw new Error('Lead not found');
  }

  const appointmentId = await appointmentService.createAppointment(
    {
      projectId: lead.projectId || '',
      leadId,
      leadName: lead.parentName,
      parentName: lead.parentName,
      phone: lead.phone,
      userId: appointmentData.clientUserId,
      // Campus → per-campus slot capacity (3pm at Metro North ≠ 3pm at Maryvale).
      ...(lead.schoolSegmentId ? { schoolSegmentId: lead.schoolSegmentId } : {}),
      ...(appointmentData.scheduleId ? { scheduleId: appointmentData.scheduleId } : {}),
      date: appointmentData.date,
      startTime: appointmentData.startTime,
      endTime: appointmentData.endTime,
      eventType: appointmentData.eventType,
      status: 'scheduled',
      ...(appointmentData.rideProvided ? { rideProvided: true } : {}),
    } as Omit<Appointment, 'id' | 'createdAt'>,
    {
      createdByUserId: scheduledByUserId,
      createdByUserName: scheduledByUserName,
      slotCapacity: appointmentData.slotCapacity ?? 1,
      // Suppress the internal notification path — we send a follow-up-specific
      // notification below via notificationService.
      skipNotifications: true,
    }
  );

  await updateDoc(doc(db, 'leads', leadId), {
    lastUpdated: Timestamp.now(),
    notes: (lead.notes || '') + `\n[${format(new Date(), 'PPP')}] Follow-up ${appointmentData.eventType} scheduled for ${appointmentData.date} at ${appointmentData.startTime}`
  });

  notificationService.sendFollowUpAppointmentNotification({
    appointmentId: appointmentId,
    leadId,
    leadName: lead.parentName || 'Lead',
    projectId: lead.projectId || '',
    date: appointmentData.date,
    startTime: appointmentData.startTime,
    endTime: appointmentData.endTime,
    eventType: appointmentData.eventType,
    clientUserId: appointmentData.clientUserId,
    clientUserName: appointmentData.clientUserName,
    scheduledByName: scheduledByUserName,
    scheduledById: scheduledByUserId
  }).catch(err => {
    console.error('Error sending follow-up notifications:', err);
  });

  return appointmentId;
}

export async function deleteLead(leadId: string): Promise<{ appointmentsDeleted: number; tasksDeleted: number }> {
  let appointmentsDeleted = 0;
  let tasksDeleted = 0;

  try {
    // Delete all appointments for this lead
    const appointmentsQuery = query(
      collection(db, 'appointments'),
      where('leadId', '==', leadId)
    );
    const appointmentsSnapshot = await getDocs(appointmentsQuery);

    if (appointmentsSnapshot.docs.length > 0) {
      // Release each slot lock before deleting so the slots free up.
      for (const docSnap of appointmentsSnapshot.docs) {
        const apt = docSnap.data() as Appointment;
        if (apt?.date && apt?.userId && apt?.startTime) {
          await appointmentService._releaseSlotLock(apt.date, apt.userId, apt.startTime, apt.schoolSegmentId);
        }
      }

      const batch = writeBatch(db);
      appointmentsSnapshot.docs.forEach(docSnap => {
        batch.delete(docSnap.ref);
        appointmentsDeleted++;
      });
      await batch.commit();
    }

    // Delete all tasks related to this lead
    const tasksQuery = query(
      collection(db, 'tasks'),
      where('leadId', '==', leadId)
    );
    const tasksSnapshot = await getDocs(tasksQuery);

    if (tasksSnapshot.docs.length > 0) {
      const batch = writeBatch(db);
      tasksSnapshot.docs.forEach(docSnap => {
        batch.delete(docSnap.ref);
        tasksDeleted++;
      });
      await batch.commit();
    }

    // Delete the lead document itself
    await deleteDoc(doc(db, 'leads', leadId));

    return { appointmentsDeleted, tasksDeleted };
  } catch (error) {
    console.error('Error deleting lead:', error);
    throw error;
  }
}

